import React from 'react';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import UserSettingsProfileModal from '../UserSettingsProfile/UserSettingsProfileModal';
import TherapistDetailModal from '../../../Manager/Components/Workers/TherapistDetail/TherapistDetailModal';
import SUKLCredentialsSetupFormModal from '../../../EPrescription/Components/SUKLCredentialsSetup/SUKLCredentialsSetupFormModal';
import SignatureModal from '../../../../Components/SignatureModal/SignatureModal';
import NCZISetupModal from '../../../Nczi/Components/NCZISetup/NCZISetupModal';
import UserSettingsPasswordModal from '../UserSettingsPassword/UserSettingsPasswordModal';
import UserSettingsConnectionsModal from '../UserSettingsConnections/UserSettingsConnectionsModal';
import UserSettingsWorkplaceModal from '../UserSettingsWorkplace/UserSettingsWorkplaceModal';
import styles from './UserSettingsWrapper.module.scss';
import { useAclContext } from '../../../Acl/Components/AclContextProvider';
import { useAppContext } from '../../../../Services/AppContext';
import NetteApiClient from '../../../../Services/NetteApiClient';
import NotificationsSetupModal from '../../../NotificationsSetup/Components/NotificationsSetupModal';
import DefaultSettingsModal from '../../../DefaultSettings/Components/DefaultSettingsModal';
import CustomSignatureModal from '../../../UserSettings/Components/CustomSignature/CustomSignatureModal';
import useScope from '../../../Scope/Hooks/useScope';
import P1DoctorSetupModal from '../../../P1/Components/P1DoctorSetup/P1DoctorSetupModal';

interface Props {
	userId: string;
	onClose: () => void;
}

const UserSettingsWrapper: React.FC<Props> = ({ userId, onClose }) => {
	const { isAllowed } = useAclContext();
	const { isTherapist, isSUKLAllowed, redirectToUserCertificates } =
		useAppContext();
	const { isLatam, isPoland } = useScope();

	const handleOpenUserCertificates = () => {
		if (redirectToUserCertificates) {
			NetteApiClient.get(redirectToUserCertificates);
			onClose();
		}
	};

	const {
		onOpen: onProfileOpen,
		isOpen: isProfileOpen,
		onClose: onProfileClose,
	} = useDisclose();
	const {
		onOpen: onTherapistOpen,
		isOpen: isTherapistOpen,
		onClose: onTherapistClose,
	} = useDisclose();
	const {
		onOpen: onWorkplaceOpen,
		isOpen: isWorkplaceOpen,
		onClose: onWorkplaceClose,
	} = useDisclose();
	const {
		onOpen: onSUKLCredentialsOpen,
		isOpen: isSUKLCredentialsOpen,
		onClose: onSUKLCredentialsClose,
	} = useDisclose();
	const {
		onOpen: onSignatureOpen,
		isOpen: isSignatureOpen,
		onClose: onSignatureClose,
	} = useDisclose();
	const {
		onOpen: onNCZIOpen,
		isOpen: isNCZIOpen,
		onClose: onNCZIClose,
	} = useDisclose();
	const {
		onOpen: onPasswordOpen,
		isOpen: isPasswordOpen,
		onClose: onPasswordClose,
	} = useDisclose();
	const {
		onOpen: onConnectionsOpen,
		isOpen: isConnectionsOpen,
		onClose: onConnectionsClose,
	} = useDisclose();
	const {
		onOpen: onNotificationSetupOpen,
		isOpen: isNotificationSetupOpen,
		onClose: onNotificationSetupClose,
	} = useDisclose();
	const {
		onOpen: onDefaultSettingsOpen,
		isOpen: isDefaultSettingsOpen,
		onClose: onDefaultSettingsClose,
	} = useDisclose();
	const {
		onOpen: onCustomSignatureOpen,
		isOpen: isCustomSignatureOpen,
		onClose: onCustomSignatureClose,
	} = useDisclose();
	const {
		onOpen: onP1SetupOpen,
		isOpen: isP1SetupOpen,
		onClose: onP1SetupClose,
	} = useDisclose();

	return (
		<div className={styles.wrapper}>
			<Button
				title='base.shared.myProfile'
				theme={Theme.BLUE}
				onClick={onProfileOpen}
			/>
			{isTherapist && (
				<Button
					title='base.myTherapistProfile'
					theme={Theme.BLUE}
					onClick={onTherapistOpen}
				/>
			)}
			{isAllowed('doctor_certificates') && (
				<Button
					title='component.certificates.myCertificates'
					theme={Theme.BLUE}
					onClick={handleOpenUserCertificates}
				/>
			)}
			<Button
				title='base.myWorkplace'
				theme={Theme.BLUE}
				onClick={onWorkplaceOpen}
			/>
			{isSUKLAllowed && (
				<Button
					title='component.certificates.loginSUKL'
					theme={Theme.BLUE}
					onClick={onSUKLCredentialsOpen}
				/>
			)}
			{isLatam && isAllowed('signature_setup') && (
				<Button
					title='base.signature'
					theme={Theme.BLUE}
					onClick={onSignatureOpen}
				/>
			)}
			{isAllowed('ezdravie_setup') && (
				<Button
					title='component.nczi.setup.button'
					theme={Theme.BLUE}
					onClick={onNCZIOpen}
				/>
			)}
			{isPoland && (
				<Button
					title='component.p1DoctorSetup.button'
					theme={Theme.BLUE}
					onClick={onP1SetupOpen}
				/>
			)}
			<Button
				title='component.notificationsSetupButton.title'
				theme={Theme.BLUE}
				onClick={onNotificationSetupOpen}
			/>
			<Button
				title='base.changePassword'
				theme={Theme.BLUE}
				onClick={onPasswordOpen}
			/>
			{isAllowed('user_api_token') && (
				<Button
					title='layout.menu.apiToken'
					theme={Theme.BLUE}
					onClick={onConnectionsOpen}
				/>
			)}
			<Button
				title='component.defaultSettings.button'
				theme={Theme.BLUE}
				onClick={onDefaultSettingsOpen}
			/>
			{isLatam && (
				<Button
					title='component.customSignature.button'
					theme={Theme.BLUE}
					onClick={onCustomSignatureOpen}
				/>
			)}
			{isProfileOpen && (
				<UserSettingsProfileModal
					userId={userId}
					isOpen
					onClose={onProfileClose}
				/>
			)}
			{isTherapistOpen && (
				<TherapistDetailModal
					userId={userId}
					isOpen
					onClose={onTherapistClose}
				/>
			)}
			{isWorkplaceOpen && (
				<UserSettingsWorkplaceModal
					userId={userId}
					isOpen
					onClose={onWorkplaceClose}
				/>
			)}
			{isSUKLCredentialsOpen && (
				<SUKLCredentialsSetupFormModal
					isOpen
					onClose={onSUKLCredentialsClose}
				/>
			)}
			{isSignatureOpen && (
				<SignatureModal userId={userId} isOpen onClose={onSignatureClose} />
			)}
			{isNCZIOpen && <NCZISetupModal isOpen onClose={onNCZIClose} />}
			{isPasswordOpen && (
				<UserSettingsPasswordModal
					userId={userId}
					isOpen
					onClose={onPasswordClose}
					onSuccess={onPasswordClose}
				/>
			)}
			{isConnectionsOpen && (
				<UserSettingsConnectionsModal
					userId={userId}
					isOpen
					onClose={onConnectionsClose}
				/>
			)}
			{isNotificationSetupOpen && (
				<NotificationsSetupModal isOpen onClose={onNotificationSetupClose} />
			)}
			{isDefaultSettingsOpen && (
				<DefaultSettingsModal isOpen onClose={onDefaultSettingsClose} />
			)}
			{isCustomSignatureOpen && (
				<CustomSignatureModal isOpen onClose={onCustomSignatureClose} />
			)}
			{isP1SetupOpen && (
				<P1DoctorSetupModal
					isOpen
					onClose={onP1SetupClose}
					onSave={onP1SetupClose}
				/>
			)}
		</div>
	);
};

export default UserSettingsWrapper;
